import { ReservationsService } from '@/api/services/reservations.service';
import type { TReservationGuestSort } from '@/api/services/reservations.service';
import { reservationStatusCounts } from '@/domain/reservations/reservation-day-grouping';
import { isLate, lateMinutes } from '@/domain/reservations/reservation-status';
import {
  addDays,
  defaultBoardDate,
} from '@/domain/reservations/reservation-time';
import { PermissionType } from '@/enums/PermissionType';
import type {
  TReservationSource,
  TReservationStatus,
} from '@/interfaces/IReservation';
import {
  getLimitArg,
  getOptionalBoolean,
  getOptionalEnum,
  getOptionalString,
  getPageArg,
  normalizeBusinessDateInput,
} from '../tool-args';
import { runToolApiCall } from '../tool-http';
import {
  capList,
  omitNullish,
  sanitizeReservationGuestRow,
  sanitizeReservationRow,
  sanitizeWaitlistRow,
} from '../sanitize';
import {
  ensureAnyPermissionOrThrow,
  ensurePermissionOrThrow,
} from '../tool-scope';
import { HostToolError } from '../tool-types';
import type {
  ICartadigitalAdminHostToolDeps,
  IGpthelpTool,
} from '../tool-types';

const STATUSES: TReservationStatus[] = [
  'pending_payment',
  'pending',
  'confirmed',
  'arrived',
  'seated',
  'finished',
  'cancelled',
  'no_show',
];
const SOURCES: TReservationSource[] = [
  'online',
  'staff',
  'phone',
  'walk_in',
  'waitlist',
];
const GUEST_SORT_FIELDS = [
  'name',
  'visits',
  'noShows',
  'lastVisit',
  'cancellations',
] as const;
const GUEST_SORT_COLUMNS: Record<
  typeof GUEST_SORT_FIELDS[number],
  TReservationGuestSort
> = {
  name: 'name',
  visits: 'visitsCount',
  noShows: 'noShowsCount',
  lastVisit: 'lastVisitAt',
  cancellations: 'cancellationsCount',
};
const ensureReservationsRead = (deps: ICartadigitalAdminHostToolDeps) =>
  ensurePermissionOrThrow({
    deps,
    permission: PermissionType.ReservationsManage,
    message: 'No tiene permisos para administrar reservas',
  });
const currentServiceDate = (
  deps: ICartadigitalAdminHostToolDeps,
  timeZone: string
) =>
  defaultBoardDate(
    new Date(),
    timeZone,
    deps.restaurantStore.selectedBranch?.businessDayStartHour
  );

export const reservationsTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'reservations_day',
      description:
        'Reservations board for one service date in the selected branch, including shifts, full-day status counts and the active waitlist. Reservation and waitlist lists each cap at 200 rows with truncated:true. Counts cover the full day even when rows are truncated. guestLateMinutes is the guest-reported delay and can be present before the booking starts; late/lateMinutes describe elapsed lateness evaluated at serverTime. Overnight bookings belong to the shift starting date; omitted date defaults to the current branch reservation business date (cutover 0 means 04:00).',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          date: {
            type: 'string',
            description:
              'Service date yyyy-MM-dd. Defaults to the current reservation business date in the selected branch.',
          },
        },
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) =>
      gate.hasRestaurantScope && gate.canManageReservations,
    async execute(args, { deps, scope }) {
      ensureReservationsRead(deps);
      const date =
        normalizeBusinessDateInput({
          obj: args,
          key: 'date',
          timeZone: scope.timeZone,
        }) ?? currentServiceDate(deps, scope.timeZone);
      const day = await runToolApiCall(() =>
        ReservationsService.getDay(scope.restaurantId, scope.branchId, date)
      );
      const toleranceMin = day.settingsSummary.toleranceMin;
      const reservations = capList(day.reservations, 200);
      const waitlist = capList(day.waitlist, 200);
      return omitNullish({
        date: day.date,
        serverTime: day.serverTime,
        toleranceMin,
        // Distinguishes "no shifts configured" from "closed this day".
        shiftsCount: day.settingsSummary.shiftsCount,
        // Whether the branch ever took a reservation (absent on older APIs).
        hasReservations: day.settingsSummary.hasReservations,
        counts: reservationStatusCounts(day.reservations),
        shifts: day.shifts.map((shift) =>
          omitNullish({
            id: shift.id,
            name: shift.name,
            from: shift.from,
            to: shift.to,
            startsAt: shift.startsAt,
            endsAt: shift.endsAt,
            maxCovers: shift.maxCovers,
            bookedCovers: shift.bookedCovers,
          })
        ),
        reservations: {
          ...reservations,
          results: reservations.results.map((row) => ({
            ...sanitizeReservationRow(row),
            late: isLate(row, day.serverTime, toleranceMin),
            lateMinutes: lateMinutes(row, day.serverTime, toleranceMin),
          })),
        },
        waitlist: {
          ...waitlist,
          results: waitlist.results.map(sanitizeWaitlistRow),
        },
      });
    },
  },
  {
    definition: {
      name: 'reservations_find',
      description:
        'Search reservations by inclusive service-date range, status, source or guest name/phone in the selected branch. guestLateMinutes is the guest-reported delay, not calculated elapsed lateness; use reservations_day for late/lateMinutes. Defaults to the next 7 service dates starting with the current reservation business date. page selects a fixed 50-row server page. limit only caps emitted rows; keep 50 for complete pagination. If truncated:true, repeat the SAME page with limit 50 to retrieve omitted rows before advancing.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          dateFrom: {
            type: 'string',
            description:
              'First service date yyyy-MM-dd, inclusive. Defaults to the current branch reservation business date.',
          },
          dateTo: {
            type: 'string',
            description:
              'Last service date yyyy-MM-dd, inclusive. Defaults to six days after dateFrom.',
          },
          status: { type: 'string', enum: STATUSES },
          source: { type: 'string', enum: SOURCES },
          search: { type: 'string', description: 'Guest name or phone.' },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 50 },
        },
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) =>
      gate.hasRestaurantScope && gate.canManageReservations,
    async execute(args, { deps, scope }) {
      ensureReservationsRead(deps);
      const from =
        normalizeBusinessDateInput({
          obj: args,
          key: 'dateFrom',
          timeZone: scope.timeZone,
        }) ?? currentServiceDate(deps, scope.timeZone);
      const to =
        normalizeBusinessDateInput({
          obj: args,
          key: 'dateTo',
          timeZone: scope.timeZone,
        }) ?? addDays(from, 6);
      if (from > to)
        throw new HostToolError(
          'INVALID_ARGS',
          '"dateFrom" must not be after "dateTo"',
          { from, to }
        );
      const status = getOptionalEnum(args, 'status', STATUSES);
      const source = getOptionalEnum(args, 'source', SOURCES);
      const search = getOptionalString(args, 'search');
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 50, max: 50 });
      const data = await runToolApiCall(() =>
        ReservationsService.getRange(
          scope.restaurantId,
          scope.branchId,
          from,
          to,
          {
            status: status ?? undefined,
            source: source ?? undefined,
            search: search ?? undefined,
          },
          page
        )
      );
      const rows = capList(data.items, limit);
      return omitNullish({
        dateFrom: from,
        dateTo: to,
        total: data.total,
        page: data.page,
        pageSize: 50,
        ...rows,
        results: rows.results.map(sanitizeReservationRow),
      });
    },
  },
  {
    definition: {
      name: 'reservations_guests',
      description:
        'Search the restaurant-wide reservation guest book by name or phone (phone matching requires at least four digits). Returns customer id, name, phone, reservation/visit/no-show/cancellation/upcoming counts, last visit and tags; email and notes are withheld. page selects a fixed 20-row server page, regardless of limit. limit only caps emitted rows, up to 50; use at least 20 for a complete page. If truncated:true, repeat the SAME page with limit 20 before advancing. total is the matching guest count across all pages.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          search: { type: 'string', description: 'Guest name or phone.' },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
          sortBy: {
            type: 'string',
            enum: GUEST_SORT_FIELDS,
            default: 'name',
          },
          sortDesc: { type: 'boolean', default: false },
        },
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) =>
      gate.hasRestaurantScope && gate.canManageReservations,
    async execute(args, { deps, scope }) {
      ensureReservationsRead(deps);
      const search = getOptionalString(args, 'search');
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 20, max: 50 });
      const sortBy =
        getOptionalEnum(args, 'sortBy', GUEST_SORT_FIELDS) ?? 'name';
      const sortDesc = getOptionalBoolean(args, 'sortDesc') ?? false;
      const data = await runToolApiCall(() =>
        ReservationsService.searchGuests(scope.restaurantId, {
          search: search ?? undefined,
          page,
          sortBy: GUEST_SORT_COLUMNS[sortBy],
          sortDesc,
        })
      );
      const guests = capList(data.items, limit);
      return omitNullish({
        total: data.total,
        page: data.page,
        pageSize: 20,
        ...guests,
        results: guests.results.map(sanitizeReservationGuestRow),
      });
    },
  },
  {
    definition: {
      name: 'reservations_stats',
      description:
        'Reservation demand, outcomes, deposits and capacity for an inclusive service-date range of at most 366 days. Uses the selected branch unless allBranches:true, which aggregates only authorized active branches in the restaurant (maximum 20; the API rejects larger scopes instead of returning partial totals). Requires ReservationsManage OR StatisticsView. perDay emits the first 62 dates with truncated:true when more exist; totals, perShift, byHour and byStatus still cover the full requested range. Request narrower date ranges for omitted daily rows. Contains aggregate data only.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          dateFrom: {
            type: 'string',
            description: 'First service date yyyy-MM-dd, inclusive.',
          },
          dateTo: {
            type: 'string',
            description:
              'Last service date yyyy-MM-dd, inclusive; at most 366 dates from dateFrom.',
          },
          allBranches: { type: 'boolean', default: false },
        },
        required: ['dateFrom', 'dateTo'],
      },
    },
    policy: 'read',
    isAvailable: (gate) =>
      gate.hasRestaurantScope &&
      (gate.canManageReservations || gate.canViewStats),
    async execute(args, { deps, scope }) {
      ensureAnyPermissionOrThrow({
        deps,
        permissions: [
          PermissionType.ReservationsManage,
          PermissionType.StatisticsView,
        ],
        message: 'No tiene permisos para ver estadísticas de reservas',
      });
      const from = normalizeBusinessDateInput({
        obj: args,
        key: 'dateFrom',
        timeZone: scope.timeZone,
      });
      const to = normalizeBusinessDateInput({
        obj: args,
        key: 'dateTo',
        timeZone: scope.timeZone,
      });
      if (!from || !to || from > to)
        throw new HostToolError(
          'INVALID_ARGS',
          'Provide dateFrom and dateTo in ascending order',
          { from, to }
        );
      const days =
        (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) /
          86400000 +
        1;
      if (days > 366)
        throw new HostToolError(
          'INVALID_ARGS',
          'The reservation report supports at most 366 inclusive service dates',
          { from, to }
        );
      const allBranches = getOptionalBoolean(args, 'allBranches') ?? false;
      const data = await runToolApiCall(() =>
        ReservationsService.getStats(
          scope.restaurantId,
          allBranches ? null : scope.branchId,
          from,
          to
        )
      );
      const perDay = capList(data.perDay, 62);
      // Keep lists explicit at every level: future server fields must not leak
      // guest details or internal metadata into an aggregate-only tool.
      return omitNullish({
        dateFrom: from,
        dateTo: to,
        allBranches,
        totals: {
          reservations: data.totals.reservations,
          covers: data.totals.covers,
          avgParty: data.totals.avgParty,
          online: data.totals.online,
          onlineShare: data.totals.onlineShare,
          walkIns: data.totals.walkIns,
          noShows: data.totals.noShows,
          noShowRate: data.totals.noShowRate,
          cancellations: data.totals.cancellations,
          cancelRate: data.totals.cancelRate,
          deposits: {
            count: data.totals.deposits.count,
            amount: data.totals.deposits.amount,
            applied: data.totals.deposits.applied,
            refunded: data.totals.deposits.refunded,
            forfeited: data.totals.deposits.forfeited,
          },
          whatsappMessages: data.totals.whatsappMessages,
        },
        perDay: perDay.results.map((day) => ({
          date: day.date,
          reservations: day.reservations,
          covers: day.covers,
          bySource: Object.fromEntries(
            SOURCES.map((source) => [source, day.bySource[source]])
          ),
        })),
        truncated: perDay.truncated,
        perShift: data.perShift.map((shift) =>
          omitNullish({
            name: shift.name,
            reservations: shift.reservations,
            covers: shift.covers,
          })
        ),
        byHour: data.byHour.map((hour) => ({
          hour: hour.hour,
          covers: hour.covers,
          capacity: hour.capacity,
        })),
        byStatus: Object.fromEntries(
          STATUSES.map((status) => [status, data.byStatus[status]])
        ),
      });
    },
  },
];
